import { t } from '@lingui/macro'

import type {
  MedicationScheme,
  MedicationSchemeStatus,
  MedicationStatement,
} from '@/modules/app/types/HealthParameter.types'

export function getMedicationDosageText(medication: MedicationStatement): string {
  const texts = (medication.dosage ?? []).map((dosage) => dosage.text).filter(Boolean)
  const joined = texts.join(', ')
  const asNeeded = (medication.dosage ?? []).some((dosage) => dosage.asNeededBoolean === true)
  const ifNeededLabel = t({
    id: 'medication.frequency.ifNeeded',
    message: 'indien nodig',
  })

  // As-needed combined with a fixed schedule: append the label as a suffix.
  if (asNeeded && joined) {
    return `${joined} (${ifNeededLabel})`
  }
  // As-needed-only (no quantities): the label is the entire dosage text.
  if (asNeeded) {
    return ifNeededLabel
  }

  return joined
}

/**
 * Returns the localized empty-state message for a given medication schemeStatus.
 *
 * - 'confirmed-empty' → "Je neemt geen medicatie"
 * - everything else   → "Geen medicatiegegevens gekend"
 */
export function getMedicationEmptyMessage(schemeStatus?: MedicationSchemeStatus): string {
  if (schemeStatus === 'confirmed-empty') {
    return t({
      id: 'observation.medication.confirmed-empty',
      message: 'Je neemt geen medicatie',
    })
  }

  return t({
    id: 'health-parameter.medication.empty-data-point',
    comment: 'Label for the empty meded data point',
    message: 'Geen medicatiegegevens gekend',
  })
}

/**
 * Formats a MedicationScheme into a human-readable text representation
 * for storage in the answer's `text` field (displayed by Synops).
 *
 * Each medication is listed on its own line as "Name - dosage text".
 */
export function formatMedicationScheduleText(schedule: MedicationScheme): string {
  const visibleMedications = schedule.medications.filter((medication) => medication.diffStatus !== 'deleted')

  if (visibleMedications.length === 0) {
    if (schedule.schemeStatus === 'confirmed-empty') {
      return t({
        id: 'observation.medication.confirmed-empty',
        message: 'Je neemt geen medicatie',
      })
    }

    return ''
  }

  return visibleMedications
    .map((medication) => {
      const dosage = getMedicationDosageText(medication)
      return dosage ? `${medication.name} - ${dosage}` : medication.name
    })
    .join('\n')
}
